import React, { useState } from "react";
import { Button, message } from "antd";
import { ArrowLeftOutlined, CheckCircleFilled } from "@ant-design/icons";
import { getAuthMethod } from "../../services/HttpServices";
import { getToken } from "../../utils/auth";
import { ReactComponent as PhoneIcon } from "../../assets/svg/whatsapp/phone.svg";
import { ReactComponent as FacebookIcon } from "../../assets/svg/whatsapp/facebook.svg";
import { ReactComponent as BusinessIcon } from "../../assets/svg/whatsapp/business.svg";
import { ReactComponent as LockIcon } from "../../assets/svg/whatsapp/lock.svg";
import { ReactComponent as TwentyFourHourIcon } from "../../assets/svg/whatsapp/24-hr.svg";
import { ReactComponent as TemplateApprovalIcon } from "../../assets/svg/whatsapp/temp-approval.svg";
import { ReactComponent as InfoIcon } from "../../assets/svg/whatsapp/info.svg";
import { ReactComponent as VerificationIcon } from "../../assets/svg/whatsapp/verification.svg";
import { ReactComponent as ClockIcon } from "../../assets/svg/whatsapp/clock.svg";

const BulletPoint = ({ children, colorClass }) => (
  <div className="tw-flex tw-items-start tw-gap-2 tw-mb-3">
    <CheckCircleFilled
      className={`tw-mt-1 ${colorClass}`}
      style={{ fontSize: "14px" }}
    />
    <div className="tw-text-sm tw-text-gray-600">{children}</div>
  </div>
);

const WhatsAppSetupFlow = ({ onBack, onFinish }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const launchWhatsAppSignup = () => {
    setIsSubmitting(true);
    
    // Using the Redirect OAuth Flow. We first fetch the secure Meta OAuth URL from the backend.
    getAuthMethod("/api/whatsapp/accounts/oauth/url", getToken())
      .then((response) => {
        if (response.data && response.data.url) {
            // Redirect the user entirely to Meta's OAuth dialog. 
            // Meta will bounce them to our ngrok backend which will 302 back to our UI.
            window.location.href = response.data.url;
        } else {
            message.error("Invalid response from server. Please try again.");
            setIsSubmitting(false);
        }
      })
      .catch((err) => {
        console.error("Failed to generate Meta OAuth URL:", err);
        message.error("Unable to start WhatsApp integration. Please try again.");
        setIsSubmitting(false);
      });
  };
  return (
    <div className="tw-p-4 md:tw-p-10 tw-h-full tw-overflow-y-auto tw-bg-white">
      <div className="tw-max-w-6xl tw-mx-auto tw-mt-4 md:tw-mt-10">
        <div className="tw-text-center tw-mb-8">
          <h2 className="tw-text-lg md:tw-text-[35px] tw-font-semibold tw-text-black tw-mb-2">
            Connect Your WhatsApp Business Account
          </h2>
          <p className="tw-text-sub-text tw-font-medium tw-text-base md:tw-text-lg tw-max-w-2xl tw-mx-auto">
            Complete the requirements below to connect your WhatsApp Business
            API and start automating bookings, campaigns, and guest
            conversations.
          </p>
        </div>

        <div className="tw-bg-white tw-border tw-border-solid tw-border-gray-200 tw-rounded-2xl tw-p-8 tw-shadow-sm">
          <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-3 tw-gap-8 tw-mb-10">
            <div className="tw-flex tw-flex-col">
              <div className="tw-w-[81px] tw-h-[81px] tw-rounded-full tw-bg-[#DFFFEB] tw-flex tw-items-center tw-justify-center tw-mb-4">
                <PhoneIcon className="tw-w-[27px] tw-h-[27px]" />
              </div>
              <h3 className="tw-font-semibold tw-text-black tw-text-base tw-mb-2">
                1. Dedicated Phone Number
              </h3>
              <p className="tw-text-text-primary tw-font-regular tw-text-sm tw-mb-6">
                Use a valid phone number that meets WhatsApp Business API
                requirements.
              </p>
              <div className="tw-flex-1">
                <BulletPoint colorClass="tw-text-[#009A1C]">
                  <span className="tw-text-text-primary">
                    Must be active and accessible
                  </span>
                </BulletPoint>
                <BulletPoint colorClass="tw-text-[#009A1C]">
                  <span className="tw-text-text-primary">
                    OTP verification will be required
                  </span>
                </BulletPoint>
                <BulletPoint colorClass="tw-text-[#009A1C]">
                  <span className="tw-text-text-primary">
                    Cannot already be connected to another WhatsApp Business
                    provider
                  </span>
                </BulletPoint>
              </div>
              <div className="tw-bg-[#ECFAF0] tw-rounded-lg tw-p-3 tw-mt-4 tw-flex tw-gap-3 tw-items-center">
                <InfoIcon className="tw-w-[21px] tw-h-[21px] tw-flex-shrink-0" />
                <div className="tw-text-xs tw-font-Regular tw-text-text-primary tw-m-0">
                  Each business requires its own dedicated WhatsApp number.
                  Existing personal WhatsApp numbers may require migration.
                </div>
              </div>
            </div>

            <div className="tw-flex tw-flex-col md:tw-border-l md:tw-border-r md:tw-border-t-0 md:tw-border-b-0 tw-border-solid tw-border-gray-200 md:tw-px-8">
              <div className="tw-w-[81px] tw-h-[81px] tw-rounded-full tw-bg-[#EEF7FF] tw-flex tw-items-center tw-justify-center tw-mb-4">
                <FacebookIcon className="tw-w-[27px] tw-h-[27px]" />
              </div>
              <h3 className="tw-font-semibold tw-text-black tw-text-base tw-mb-2">
                2. Facebook Verification
              </h3>
              <p className="tw-text-text-primary tw-font-regular tw-text-sm tw-mb-6">
                Connect your Meta Business account to continue setup.
              </p>
              <div className="tw-flex-1">
                <BulletPoint colorClass="tw-text-[#0064E0]">
                  <span className="tw-text-text-primary">
                    Login with your Facebook business account
                  </span>
                </BulletPoint>
                <BulletPoint colorClass="tw-text-[#0064E0]">
                  <span className="tw-text-text-primary">
                    Account should be connected to your business email
                  </span>
                </BulletPoint>
                <BulletPoint colorClass="tw-text-[#0064E0]">
                  <span className="tw-text-text-primary">
                    Authentication or verification code may be required
                  </span>
                </BulletPoint>
              </div>
              <div className="tw-bg-[#EFF4FE] tw-rounded-lg tw-p-3 tw-mt-4 tw-flex tw-gap-3 tw-items-center">
                <LockIcon className="tw-w-[16px] tw-h-[21px] tw-flex-shrink-0" />
                <div className="tw-text-xs tw-font-Regular tw-text-text-primary tw-m-0">
                  We never post or message customers without your permission.
                </div>
              </div>
            </div>

            <div className="tw-flex tw-flex-col">
              <div className="tw-w-[81px] tw-h-[81px] tw-rounded-full tw-bg-[#F3F2FF] tw-flex tw-items-center tw-justify-center tw-mb-4">
                <BusinessIcon className="tw-w-[27px] tw-h-[27px]" />
              </div>
              <h3 className="tw-font-semibold tw-text-black tw-text-base tw-mb-2">
                3. Business Details
              </h3>
              <p className="tw-text-text-primary !tw-font-regular tw-text-sm tw-mb-6">
                Keep your business information ready for verification.
              </p>
              <div className="tw-flex-1">
                <BulletPoint colorClass="tw-text-[#5D44FF]">
                  <span className="tw-text-text-primary">
                    Legal business name
                  </span>
                </BulletPoint>
                <BulletPoint colorClass="tw-text-[#5D44FF]">
                  <span className="tw-text-text-primary">Business address</span>
                </BulletPoint>
                <BulletPoint colorClass="tw-text-[#5D44FF]">
                  <span className="tw-text-text-primary">Website</span>
                </BulletPoint>
                <BulletPoint colorClass="tw-text-[#5D44FF]">
                  <span className="tw-text-text-primary">
                    WhatsApp display name
                  </span>
                </BulletPoint>
                <BulletPoint colorClass="tw-text-[#5D44FF]">
                  <span className="tw-text-text-primary">
                    Business category
                  </span>
                </BulletPoint>
              </div>
              <div className="tw-bg-[#FDF3E1] tw-rounded-lg tw-p-3 tw-mt-4 tw-flex tw-gap-3 tw-items-center">
                <VerificationIcon className="tw-w-[21px] tw-h-[21px] tw-flex-shrink-0" />
                <div className="tw-text-xs tw-font-Regular tw-text-text-primary tw-m-0">
                  Meta verification is recommended to unlock higher messaging
                  limits and better account trust.
                </div>
              </div>
            </div>
          </div>

          <div className="tw-bg-[#F6F6FE] tw-rounded-xl tw-p-6 tw-grid tw-grid-cols-1 md:tw-grid-cols-3 tw-gap-6 md:tw-divide-x md:tw-divide-y-0 tw-divide-x-0 tw-divide-y tw-divide-solid tw-divide-gray-200">
            <div className="tw-flex tw-gap-4 md:tw-pr-6">
              <div className="tw-w-[38px] tw-h-[38px] tw-rounded-full tw-border tw-border-solid tw-border-theme tw-flex tw-items-center tw-justify-center tw-flex-shrink-0">
                <TwentyFourHourIcon className="tw-w-[16px] tw-h-[16px]" />
              </div>
              <div>
                <h4 className="tw-text-theme tw-font-bold tw-text-xs tw-mb-1">
                  24-Hour Reply Window
                </h4>
                <p className="tw-text-text-primary tw-font-regular tw-text-xs tw-m-0">
                  Custom replies are only allowed within 24 hours of the
                  customer's last message.
                </p>
              </div>
            </div>

            <div className="tw-flex tw-gap-4 md:tw-px-6 tw-pt-4 md:tw-pt-0">
              <div className="tw-w-[38px] tw-h-[38px] tw-rounded-full tw-border tw-border-solid tw-border-theme tw-flex tw-items-center tw-justify-center tw-flex-shrink-0">
                <TemplateApprovalIcon className="tw-w-[20px] tw-h-[20px]" />
              </div>
              <div>
                <h4 className="tw-text-theme tw-font-bold tw-text-xs tw-mb-1">
                  Template Approval Required
                </h4>
                <p className="tw-text-text-primary tw-font-regular tw-text-xs tw-m-0">
                  Promotional and automated messages require Meta approval
                  before sending.
                </p>
              </div>
            </div>

            <div className="tw-flex tw-gap-4 md:tw-pl-6 tw-pt-4 md:tw-pt-0">
              <div className="tw-w-[38px] tw-h-[38px] tw-rounded-full tw-border tw-border-solid tw-border-theme tw-flex tw-items-center tw-justify-center tw-flex-shrink-0">
                <ClockIcon className="tw-w-[18px] tw-h-[18px]" />
              </div>
              <div>
                <h4 className="tw-text-theme tw-font-bold tw-text-xs tw-mb-1">
                  Template Review Time
                </h4>
                <p className="tw-text-text-primary tw-font-regular tw-text-xs tw-m-0">
                  Template approval can take up to 48 hours.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="tw-flex tw-justify-between tw-items-center tw-mt-8">
          <Button
            className="tw-flex !tw-text-lg tw-items-center tw-h-11 tw-px-6 tw-rounded-lg tw-bg-[#EEEEEE] hover:!tw-bg-[#EEEEEE] !tw-border-none tw-font-semibold tw-text-[#565656]"
            onClick={onBack}
          >
            <ArrowLeftOutlined /> Back
          </Button>

          <Button
            type="primary"
            loading={isSubmitting}
            className="tw-h-11 tw-px-8 !tw-text-lg tw-rounded-lg !tw-bg-[#0D8B4E] hover:!tw-bg-[#0D8B4E] !tw-text-white !tw-border-none tw-font-semibold"
            onClick={launchWhatsAppSignup}
          >
            Continue with Meta
          </Button>
        </div>
      </div>
    </div>
  );
};

export default WhatsAppSetupFlow;
